import { getApiUrl } from "@/utils/api";
import { useState, useEffect } from "react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import {
  Eye, Users, MousePointer, Clock, TrendingUp, TrendingDown,
  Minus, ArrowUpRight, ArrowDownRight, Loader2, RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import ToolSearchStats from "@/components/admin/analytics/ToolSearchStats";

const API = getApiUrl();

const PERIOD_OPTIONS = [
  { value: "7d", label: "7 Days" },
  { value: "30d", label: "30 Days" },
  { value: "90d", label: "90 Days" },
];

function formatSeconds(s) {
  if (!s) return "0s";
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

function formatDate(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function KpiCard({ icon: Icon, label, value, change, sub, color = "blue" }) {
  const colors = {
    blue: { bg: "bg-blue-50 border-blue-100", icon: "text-blue-600 bg-blue-100", val: "text-blue-900" },
    green: { bg: "bg-emerald-50 border-emerald-100", icon: "text-emerald-600 bg-emerald-100", val: "text-emerald-900" },
    amber: { bg: "bg-amber-50 border-amber-100", icon: "text-amber-600 bg-amber-100", val: "text-amber-900" },
    purple: { bg: "bg-purple-50 border-purple-100", icon: "text-purple-600 bg-purple-100", val: "text-purple-900" },
    rose: { bg: "bg-rose-50 border-rose-100", icon: "text-rose-600 bg-rose-100", val: "text-rose-900" },
    teal: { bg: "bg-teal-50 border-teal-100", icon: "text-teal-600 bg-teal-100", val: "text-teal-900" },
  };
  const c = colors[color] || colors.blue;
  const isPositive = change > 0;
  const isNeutral = change === 0 || change === undefined;

  return (
    <div className={`border rounded-lg p-4 ${c.bg}`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between mb-3">
        <div className={`p-2 rounded-lg ${c.icon}`}>
          <Icon className="h-4 w-4" />
        </div>
        {change !== undefined && (
          <div className={`flex items-center gap-0.5 text-xs font-medium px-2 py-0.5 rounded-full ${
            isNeutral ? "bg-gray-100 text-gray-500" :
            isPositive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
          }`}>
            {isNeutral ? <Minus className="h-3 w-3" /> : isPositive ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
            {isNeutral ? "—" : `${Math.abs(change)}%`}
          </div>
        )}
      </div>
      <p className={`text-2xl font-bold font-serif ${c.val}`}>
        {typeof value === "number" ? value.toLocaleString() : value}
      </p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-border shadow-md p-2 rounded text-xs">
      <p className="font-medium mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{p.name}: <strong>{p.value}</strong></p>
      ))}
    </div>
  );
}

export default function AnalyticsOverview({ period, onPeriodChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/analytics/overview?period=${period}`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [period]);

  const chartData = (data?.chart_data || []).map((d) => ({ ...d, label: formatDate(d.date) }));
  const tickInterval = chartData.length > 14 ? Math.floor(chartData.length / 7) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-overview">
      {/* Period toggle */}
      <div className="flex items-center gap-1">
        {PERIOD_OPTIONS.map((p) => (
          <button
            key={p.value}
            onClick={() => onPeriodChange(p.value)}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
              period === p.value ? "bg-primary text-white" : "bg-muted text-muted-foreground hover:bg-muted/80"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        <KpiCard icon={Users} label="Sessions" value={data?.total_sessions ?? 0} change={data?.sessions_change} color="blue" />
        <KpiCard icon={Eye} label="Page Views" value={data?.total_page_views ?? 0} change={data?.views_change} color="teal" />
        <KpiCard icon={MousePointer} label="Unique Visitors" value={data?.unique_visitors ?? 0} color="green" />
        <KpiCard icon={TrendingDown} label="Bounce Rate" value={`${data?.bounce_rate ?? 0}%`}
          sub={data?.bounce_rate > 60 ? "High — needs attention" : "Healthy"} color={data?.bounce_rate > 60 ? "rose" : "green"} />
        <KpiCard icon={Clock} label="Avg Duration" value={formatSeconds(data?.avg_session_duration)} color="purple" />
        <KpiCard icon={TrendingUp} label="Conversion Rate" value={`${data?.conversion_rate ?? 0}%`} color="amber" />
      </div>

      {/* Trend Chart */}
      <div className="bg-white border border-border p-6 rounded-lg">
        <h3 className="font-serif text-base font-medium mb-4">Sessions & Page Views Over Time</h3>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="gradSessions" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradViews" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#2f4f4f" stopOpacity={0.12} />
                <stop offset="95%" stopColor="#2f4f4f" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0ede8" />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={tickInterval} />
            <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
            <Tooltip content={<ChartTooltip />} />
            <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
            <Area type="monotone" dataKey="sessions" name="Sessions" stroke="#3b82f6" fill="url(#gradSessions)" strokeWidth={2} />
            <Area type="monotone" dataKey="views" name="Page Views" stroke="#2f4f4f" fill="url(#gradViews)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Visitor type */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-border p-5 rounded-lg">
          <h3 className="font-serif text-sm font-medium mb-3">Visitor Type</h3>
          <div className="space-y-3">
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-muted-foreground">New Visitors</span>
                <span className="font-medium">{data?.new_visitor_pct ?? 0}%</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${data?.new_visitor_pct ?? 0}%` }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-muted-foreground">Returning Visitors</span>
                <span className="font-medium">{100 - (data?.new_visitor_pct ?? 0)}%</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${100 - (data?.new_visitor_pct ?? 0)}%` }} />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-border p-5 rounded-lg">
          <h3 className="font-serif text-sm font-medium mb-3">Conversion Summary</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Sessions</span>
              <span className="font-medium">{(data?.total_sessions ?? 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Converted Sessions</span>
              <span className="font-medium text-emerald-700">{(data?.converted_sessions ?? 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between border-t pt-2">
              <span className="text-muted-foreground font-medium">Conversion Rate</span>
              <span className="font-bold text-primary">{data?.conversion_rate ?? 0}%</span>
            </div>
          </div>
        </div>
      </div>

      <ToolSearchStats />
    </div>
  );
}
